import { useState } from 'react'
import { X } from 'lucide-react'
import { Button } from '@trafiflow/shared-ui'
import { DialogPortal } from './DialogPortal'

interface ConfirmModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => Promise<void> | void
  title: string
  description: string
  confirmLabel?: string
  cancelLabel?: string
  variant?: 'danger' | 'primary'
}

export function ConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'primary',
}: ConfirmModalProps) {
  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const handleConfirm = async () => {
    setLoading(true)
    setErrorMessage(null)
    try {
      await onConfirm()
      onClose()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Operation failed. Please try again.'
      setErrorMessage(message)
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setErrorMessage(null)
    onClose()
  }

  if (!isOpen) return null

  return (
    <DialogPortal>
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50 animate-fade-in" onClick={handleClose} />
      <div className="relative z-10 w-full max-w-md bg-fn-card rounded-lg shadow-fn-modal border border-fn-border animate-scale-in">
        <div className="flex items-center justify-between px-6 py-4 border-b border-fn-border">
          <h2 className="text-lg font-semibold text-fn-text">{title}</h2>
          <button onClick={handleClose} className="p-1 rounded hover:bg-fn-hover transition-colors">
            <X className="w-5 h-5 text-fn-text-muted" />
          </button>
        </div>
        <div className="px-6 py-5">
          <p className="text-sm text-fn-text-sub">{description}</p>
          {errorMessage && (
            <p className="text-sm text-red-600 mt-2 px-3 py-2 bg-tf-feedback-error-bg border border-tf-feedback-error-border rounded-md">
              {errorMessage}
            </p>
          )}
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-fn-border">
          <Button variant="secondary" onClick={handleClose} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={loading}
            className={variant === 'danger' ? 'bg-red-600 text-white hover:bg-red-700' : ''}
          >
            {loading ? 'Processing...' : confirmLabel}
          </Button>
        </div>
      </div>
    </div>
    </DialogPortal>
  )
}
